import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { CalendarCheck, Loader2, Pencil, Plus, Power, PowerOff, RefreshCw } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { ActionButton } from '@components/Table';
import { usePermissions } from '@hooks/usePermissions';
import accountService from '@services/accountService';
import categoryService from '@services/categoryService';
import creditCardService from '@services/creditCardService';
import recurringService from '@services/recurringService';
import { RECURRING_TARGET_TYPE_LABELS, recurringToInput, type Account, type Category, type CreditCard, type RecurringTargetType, type RecurringTransaction } from '@/types/finance';
import { competenceLabel } from '@/utils/competence';
import { formatDateBR } from '@/utils/formatters';
import { AmountText, ColorChip } from './financeBits';
import { RecurringFormModal } from './RecurringFormModal';

const periodLabel = (r: RecurringTransaction) => `${competenceLabel(r.start_competence)} a ${r.end_competence ? competenceLabel(r.end_competence) : 'sem fim'}`;

/**
 * Finanças → Recorrências: lançamentos mensais estimados em conta ou cartão (gerados 12 meses à
 * frente pelo sistema). Filtro por alvo, inativas, novo/editar (com competências puladas), inativar
 * e reativar.
 */
export const RecurringView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('financas.write').hasPermission;
  const [items, setItems] = useState<RecurringTransaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [cards, setCards] = useState<CreditCard[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [targetType, setTargetType] = useState<RecurringTargetType | ''>('');
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<{ recurring?: RecurringTransaction } | null>(null);
  const [deactivating, setDeactivating] = useState<RecurringTransaction | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await recurringService.getAll(showInactive, targetType));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [showInactive, targetType]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    accountService.getAll().then(setAccounts).catch(setError);
    creditCardService.getAll().then(setCards).catch(setError);
    categoryService.getAll().then(setCategories).catch(setError);
  }, []);

  const confirmDeactivate = async () => {
    if (!deactivating) return;
    setBusy(deactivating.id);
    try {
      await recurringService.deactivate(deactivating.id);
      setDeactivating(null);
      load();
    } catch (err) {
      setDeactivating(null);
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const reactivate = async (r: RecurringTransaction) => {
    setBusy(r.id);
    try {
      await recurringService.update(r.id, recurringToInput(r, { is_active: true }));
      load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const monthlyTotal = useMemo(() => items.filter((r) => r.is_active !== false).reduce((s, r) => s + r.amount_cents, 0), [items]);

  const actions = (r: RecurringTransaction) =>
    canWrite ? (
      <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
        <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip="Editar" onClick={() => setForm({ recurring: r })} />
        {r.is_active === false ? (
          <ActionButton icon={<Power size={16} />} variant="view" tooltip="Reativar" disabled={busy === r.id} onClick={() => reactivate(r)} />
        ) : (
          <ActionButton icon={<PowerOff size={16} />} variant="delete" tooltip="Inativar" disabled={busy === r.id} onClick={() => setDeactivating(r)} />
        )}
      </div>
    ) : null;

  const schedulingBadge = (r: RecurringTransaction) =>
    r.requires_scheduling ? (
      <span className="badge-info ml-1 gap-1" title={`Pago por agendamento no banco: avisa ${r.schedule_lead_days ?? 5} dias antes do vencimento`}>
        <CalendarCheck size={11} /> Agendamento
      </span>
    ) : null;

  const skipsCell = (r: RecurringTransaction) =>
    r.skips.length > 0 ? (
      <span className="badge-warning" title={`Puladas: ${[...r.skips].sort((a, b) => a - b).map(competenceLabel).join(', ')}`}>
        {r.skips.length} {r.skips.length === 1 ? 'pulada' : 'puladas'}
      </span>
    ) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <RefreshCw size={20} className="text-slate-500" /> Recorrências
        </h1>
        <PermissionGuard permission="financas.write">
          <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setForm({})} disabled={accounts.length === 0 && cards.length === 0}>
            Nova recorrência
          </Button>
        </PermissionGuard>
      </div>

      <div className="card p-4 grid grid-cols-1 sm:flex sm:flex-wrap sm:items-end gap-3">
        <div className="sm:w-48">
          <label className="text-label block mb-1">Onde</label>
          <select className="select-field" value={targetType} onChange={(e) => setTargetType(e.target.value as RecurringTargetType | '')}>
            <option value="">Contas e cartões</option>
            <option value="account">Só contas</option>
            <option value="card">Só cartões</option>
          </select>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer sm:pb-2.5">
          <input type="checkbox" className="checkbox-field" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Mostrar inativas
        </label>
        <p className="text-small sm:ml-auto sm:pb-2.5">
          Estimativa mensal das ativas: <AmountText cents={monthlyTotal} muted={monthlyTotal === 0} className="font-medium" />
        </p>
      </div>

      <p className="text-small">
        O valor é uma <strong>estimativa</strong>: cada mês vira um lançamento previsto (conta) ou item de fatura (cartão) com a etiqueta <em>Recorrente</em>. Ao editar um lançamento gerado, ele
        deixa de ser atualizado pela recorrência; marcar como <em>agendada</em> não desvincula. As marcadas como <em>Agendamento</em> são pagas por agendamento no
        banco: cada mês gerado avisa (painel Hoje, sino e e-mail) enquanto não for marcado como agendado.
      </p>

      {/* Celular: cartões */}
      <div className="md:hidden space-y-2">
        {loading && items.length === 0 && (
          <p className="text-center text-slate-400 py-6">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        )}
        {!loading && items.length === 0 && <p className="text-center text-slate-500 py-8">Nenhuma recorrência.</p>}
        {items.map((r) => (
          <div key={r.id} className={clsx('card p-3 space-y-1', (loading || r.is_active === false) && 'opacity-60', canWrite && 'cursor-pointer')} onClick={() => canWrite && setForm({ recurring: r })}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm text-slate-800 min-w-0 [overflow-wrap:anywhere]">
                {r.description}
                {schedulingBadge(r)}
                {r.is_active === false && <span className="badge-neutral ml-1">Inativa</span>}
              </p>
              <AmountText cents={r.amount_cents} className="text-sm font-medium" />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-small">
              <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                <ColorChip color={r.target_color}>
                  {RECURRING_TARGET_TYPE_LABELS[r.target_type]} · {r.target_name}
                </ColorChip>
                <span>dia {r.transaction_day}</span>
                <span>{periodLabel(r)}</span>
                {r.next_date && <span>próxima {formatDateBR(r.next_date)}</span>}
                {r.category_name && <span>{r.category_name}</span>}
                {skipsCell(r)}
              </span>
              {actions(r)}
            </div>
          </div>
        ))}
      </div>

      {/* Tabela */}
      <div className="table-container bg-white hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th>Descrição</th>
                <th>Onde</th>
                <th className="w-16">Dia</th>
                <th className="text-right">Valor</th>
                <th>Categoria</th>
                <th>Período</th>
                <th>Próxima</th>
                <th className="w-24">Ativa</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan={9} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={9} className="table-cell text-center py-12 text-slate-500">
                    Nenhuma recorrência. Use "Nova recorrência" para aluguel, assinaturas, salário…
                  </td>
                </tr>
              )}
              {items.map((r) => (
                <tr key={r.id} className={clsx('table-row', (loading || r.is_active === false) && 'opacity-60', canWrite && 'cursor-pointer')} onClick={() => canWrite && setForm({ recurring: r })}>
                  <td className="table-cell [overflow-wrap:anywhere]">
                    {r.description}
                    {schedulingBadge(r)}
                    {skipsCell(r) && <span className="ml-1">{skipsCell(r)}</span>}
                  </td>
                  <td className="table-cell">
                    <ColorChip color={r.target_color}>{r.target_name}</ColorChip>
                    <span className="block text-[11px] text-slate-400">{RECURRING_TARGET_TYPE_LABELS[r.target_type]}</span>
                  </td>
                  <td className="table-cell tabular-nums">{r.transaction_day}</td>
                  <td className="table-cell text-right">
                    <AmountText cents={r.amount_cents} className="font-medium" />
                  </td>
                  <td className="table-cell">{r.category_name ?? <span className="text-slate-400">—</span>}</td>
                  <td className="table-cell whitespace-nowrap">{periodLabel(r)}</td>
                  <td className="table-cell whitespace-nowrap">{r.next_date ? formatDateBR(r.next_date) : <span className="text-slate-400">—</span>}</td>
                  <td className="table-cell">{r.is_active === false ? <span className="badge-neutral">Inativa</span> : <span className="badge-success">Ativa</span>}</td>
                  <td className="table-cell">{actions(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {form && (
        <RecurringFormModal
          recurring={form.recurring}
          accounts={accounts}
          cards={cards}
          categories={categories}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            load();
          }}
          onError={setError}
        />
      )}

      <ConfirmDialog
        isOpen={!!deactivating}
        onClose={() => setDeactivating(null)}
        onConfirm={confirmDeactivate}
        title="Inativar recorrência"
        message={`Inativar "${deactivating?.description ?? ''}"? Os lançamentos futuros ainda não editados são excluídos; os passados ficam. Pode reativar depois.`}
        confirmText="Inativar"
        severity="warning"
        loading={busy !== null}
      />

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
